import type { DocumentSchema, InputNode, SectionNode } from '../../documents/types';
import type { PolicyDocumentMeta } from './PolicyDocumentView';
import {
    isEmptyValue,
    formatDate,
    evaluateCondition,
    resolveFixedOptionLabel,
} from '../utils/documentRendererUtils';
import { formatMRIParameters } from '../../reports/cpt/utils/cptDocumentViewUtils';
import { formatConditionalsListText } from '../../preview/conditionalsLists';
import { stripHtml, wrapLine } from '../../documents/utils/documentViewUtils';

const SEPARATOR = '————————————————————————————————————————————————————————';
const LINE_WIDTH = 80;

function wrapValue(text: string, indent: number = 0): string {
    const prefix = ' '.repeat(indent);
    return text
        .split('\n')
        .map((line) => wrapLine(line, LINE_WIDTH - indent))
        .join('\n')
        .split('\n')
        .map((line) => (line ? `${prefix}${line}` : line))
        .join('\n');
}

function formatPlainValue(field: InputNode, value: any, t: TFunc): string {
    if (isEmptyValue(value)) return '';

    if (
        (field.inputType === 'richtext' ||
            field.inputType === 'richtext-enhanced') &&
        typeof value === 'string'
    ) {
        return stripHtml(value).trim();
    }

    if (field.inputType === 'date') return formatDate(String(value));

    if (field.inputType === 'select' || field.inputType === 'radio') {
        const label = resolveFixedOptionLabel(field, value);
        return typeof label === 'string' ? label : String(value);
    }

    if (field.inputType === 'tag-list' && Array.isArray(value)) {
        const fixed = field.data?.options?.fixed;
        return value
            .map(
                (v: string) =>
                    fixed?.find((o: any) => o.value === v)?.label ?? v
            )
            .map((label: string) => `- ${label}`)
            .join('\n');
    }

    if (Array.isArray(value)) {
        if (value.length > 0 && typeof value[0] !== 'object')
            return value.join(', ');
        return value
            .map((item) =>
                typeof item === 'object'
                    ? Object.values(item)
                          .filter((v) => !isEmptyValue(v))
                          .join(' — ')
                    : String(item)
            )
            .join('\n');
    }

    if (typeof value === 'boolean') {
        return value ? t('preview.common.yes') : t('preview.common.no');
    }

    return String(value);
}

function buildSectionLines(
    section: SectionNode,
    data: Record<string, any>,
    _t: TFunc
): string[] {
    const lines: string[] = [];
    const sectionFields = Array.isArray(section.fields) ? section.fields : [];

    for (const node of sectionFields) {
        if (node.hidden && evaluateCondition(node.hidden, data)) continue;

        if (node.kind === 'action') {
            if (node.actionType === 'checkbox' && data[node.id]) {
                lines.push('');
                lines.push(`[x] ${node.title}`);
            }
            continue;
        }

        if (node.kind === 'view') {
            if (node.viewType !== 'total-summary') continue;
            const dependency = node.viewProps?.dependency || node.id;
            const items = Array.isArray(data[dependency])
                ? data[dependency]
                : [];
            const sumField = node.viewProps?.sumField;
            if (!items.length || !sumField) continue;
            const total = items.reduce(
                (sum: number, item: any) => sum + (Number(item[sumField]) || 0),
                0
            );
            lines.push('');
            lines.push(
                `${node.title}: ${total}${node.viewProps?.unit ? ` ${node.viewProps.unit}` : ''}`
            );
            continue;
        }

        if (node.kind !== 'input') continue;

        const value = data[node.id];

        if (node.functionKey === 'conditionals') {
            if (!value || Object.keys(value).length === 0) continue;
            lines.push('');
            if (node.title) lines.push(node.title);
            const formatted = formatMRIParameters(value);
            for (const [label, rawValue] of Object.entries(formatted)) {
                const listText = formatConditionalsListText(label, value, _t);
                if (listText) {
                    lines.push('');
                    lines.push(`    ${label}`);
                    lines.push(wrapValue(listText, 8));
                    continue;
                }

                const text = stripHtml(String(rawValue ?? '')).trim();
                if (!text) continue;
                lines.push('');
                lines.push(`    ${label}`);
                lines.push(wrapValue(text, 8));
            }
            continue;
        }

        if (node.inputType === 'repeater') {
            const items = Array.isArray(value) ? value : [];
            if (!items.length) continue;
            const children = (node.fields ?? []).filter(
                (c) => c.kind === 'input'
            ) as InputNode[];

            lines.push('');
            if (node.title) lines.push(node.title);
            items.forEach((item, index) => {
                const visibleChildren = children.filter((child) => {
                    if (
                        child.hidden &&
                        evaluateCondition(child.hidden, data, item)
                    )
                        return false;
                    return !isEmptyValue(item?.[child.id]);
                });
                if (!visibleChildren.length) return;
                if (index > 0) lines.push('');
                for (const child of visibleChildren) {
                    const text = formatPlainValue(child, item[child.id], _t);
                    if (!text) continue;
                    lines.push(wrapValue(`${child.title}: ${text}`, 4));
                }
            });
            continue;
        }

        if (isEmptyValue(value)) continue;

        const text = formatPlainValue(node, value, _t);
        if (!text) continue;

        lines.push('');
        if (node.title) lines.push(node.title);
        lines.push(wrapValue(text, node.title ? 4 : 0));
    }

    return lines;
}

type TFunc = (key: string, opts?: Record<string, any>) => string;

export interface PolicyPlainTextInput {
    schema: DocumentSchema;
    data: Record<string, any>;
    meta: PolicyDocumentMeta;
}

export function buildPolicyPlainText({
    schema,
    data,
    meta,
    t,
}: PolicyPlainTextInput & { t: TFunc }): string {
    const parts: string[] = [];

    const headerLines: string[] = [];
    headerLines.push((meta.title || t('departmentPolicies.preview.policyFallback')).toUpperCase());
    headerLines.push(SEPARATOR);
    if (meta.effectiveDate)
        headerLines.push(`${t('departmentPolicies.preview.effective')}:  ${formatDate(meta.effectiveDate)}`);
    if (meta.expiryDate)
        headerLines.push(`${t('departmentPolicies.preview.expires')}:    ${formatDate(meta.expiryDate)}`);
    if (meta.authors?.length)
        headerLines.push(`${t('departmentPolicies.preview.authors')}:    ${meta.authors.join(', ')}`);
    parts.push(headerLines.join('\n'));

    const schemaSections = Array.isArray(schema.sections)
        ? schema.sections
        : [];
    const visibleSections = schemaSections.filter((section) => {
        if (section.sectionType === 'actions') return false;
        if (section.hidden && evaluateCondition(section.hidden, data))
            return false;
        return true;
    });

    for (const section of visibleSections) {
        const sectionLines = buildSectionLines(section, data, t);
        if (!sectionLines.some((line) => line.trim())) continue;

        const block: string[] = [''];
        if (!section.style?.hideTitle && section.title) {
            block.push(section.title.toUpperCase());
            block.push(SEPARATOR);
        }
        block.push(...sectionLines);
        parts.push(block.join('\n'));
    }

    return parts.join('\n');
}
